import React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";
import { useActionLoggerOptional } from "@/services/logger/logger-hooks";
import { useRef } from "react";
import type {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { loggerContextManager } from "@/services/logger/smart-context-provider";
import { capitalizeInitialLetter } from "@/lib/string-util";

function normalizeTabTitle(text: string) {
  return text.trim().replace(/\s+/g, " ");
}

type TabLogMeta = {
  tag?: LogTags;
  category?: LogCategories;
  subCategory?: LogSubCategories;
};

type TabsProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
  logMetaByValue?: Record<string, TabLogMeta>;
};

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ onValueChange, logMetaByValue, ...props }, ref) => {
  const { logAction } = useActionLoggerOptional();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const lastValueRef = useRef<string | undefined>(undefined);
  const setRefs = (node: HTMLDivElement | null) => {
    rootRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  // Apply initial/controlled tab meta to global context so children logs
  // (inside the default active tab) get the right category/tag/subCategory
  // even before the first user tab switch.
  React.useEffect(() => {
    const initialValue =
      (props as { value?: string; defaultValue?: string }).value ??
      (props as { value?: string; defaultValue?: string }).defaultValue;
    if (!initialValue) return;

    const meta = logMetaByValue?.[initialValue];
    if (!meta) return;

    loggerContextManager.setGlobalContext({
      ...(meta.category ? { category: meta.category } : {}),
      ...(meta.tag ? { tag: meta.tag } : {}),
      ...(meta.subCategory ? { subCategory: meta.subCategory } : {}),
    });
  }, [logMetaByValue, props]);

  return (
    <TabsPrimitive.Root
      ref={setRefs}
      onValueChange={(value) => {
        // Avoid duplicate logs for same value (Radix can call onValueChange with same value).
        if (lastValueRef.current === value) {
          onValueChange?.(value);
          return;
        }
        lastValueRef.current = value;

        // The DOM "active" state may update after this callback; defer read.
        requestAnimationFrame(() => {
          const root = rootRef.current;
          const activeEl =
            root?.querySelector('[role="tab"][data-state="active"]') ||
            root?.querySelector('[role="tab"][aria-selected="true"]');

          const tabText = activeEl
            ? normalizeTabTitle(activeEl.textContent ?? "")
            : "";

          const activeTestId =
            (activeEl as HTMLElement | null)?.getAttribute?.("data-testid") ??
            undefined;

          // Build a stable, meaningful title:
          // - Prefer the data-testid constant when present (for analytics/searchability).
          // - Fall back to the visible tab text.
          // - Finally fall back to a generic label.
          const baseTitle = capitalizeInitialLetter(
            activeTestId && activeTestId.trim().length > 0
              ? `${activeTestId.split("-").join(" ")} selected`
              : tabText
                ? `${tabText.toUpperCase()} selected`
                : "tab selected",
          );

          // Update global logging context for this tab value if configured.
          const meta = logMetaByValue?.[value];
          if (meta) {
            loggerContextManager.setGlobalContext({
              ...(meta.category ? { category: meta.category } : {}),
              ...(meta.tag ? { tag: meta.tag } : {}),
              ...(meta.subCategory ? { subCategory: meta.subCategory } : {}),
            });
          }

          // For keyboard navigation, onValueChange still fires and we can read activeEl here.
          logAction(
            baseTitle,
            {
              // tabValue: value,
              // tabText,
              // tabTestId: activeTestId,
            },
            { debounceMs: 300, suppressDuplicate: true, useTitleAsIs: true },
          );
        });
        onValueChange?.(value);
      }}
      {...props}
    />
  );
});
Tabs.displayName = "Tabs";

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
